import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import {
    AccountBalanceWallet,
    CheckCircle,
    CloudDownload,
    CreditCard,
    Dangerous,
    Dns,
    Fullscreen,
    ReportProblem,
} from '@mui/icons-material';
import { Box, Stack } from '@mui/material';
import {
    RequestStatus,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    CloudConnectorPeripheryEvent,
    CloudConnectorPeripheryStateEventTypeCategory,
    CloudConnectorPeripheryType,
    getCloudConnectorEventTypeTranslation,
    getMainEventfromEvents,
    getPeripheryStatefromMainCategory,
    PeripheryStatus,
    SpecialVehicleType,
} from 'dg-web-shared/model/CloudConnector.ts';
import { Theme, useTheme } from '@mui/material/styles';
import { ReactNode, useEffect, useState } from 'react';
import { ParkingPortalLayoutWithHeader } from '../mobile/layout/ParkingPortalLayoutWithHeader.tsx';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import { generatePath, Outlet, useNavigate } from 'react-router-dom';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { HopperConfigType } from 'dg-web-shared/model/ZoneConfiguration.ts';
import {
    PayStationPaymentMethod,
    PayStationPaymentState,
} from 'dg-web-shared/model/PayStationPayment.ts';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { useDocumentVisibilityEffect } from 'dg-web-shared/common/hooks/useDocumentVisibility.ts';
import { GroupSegment } from '../shared-mui-components/GroupSegment.tsx';
import { IdentificationType } from '../common/components/IdentificationItems.tsx';
import { PaymentChannel } from '../common/models/PaymentChannel.ts';
import { IdentificationMedia } from 'dg-web-shared/model/IdentificationMedia.ts';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { ButtonWithDropdown } from '../layout/components/ButtonWithDropdown.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';

export interface OperatorPeripheriesList {
    name: string;
    zoneId: number;
    peripheryConfigs: OperatorZoneBarrierGatePeriphery[];
}

export interface OperatorZoneBarrierGatePeriphery {
    zoneId: number;
    onPremiseId: number;
    peripheryType: CloudConnectorPeripheryType;
    name: string;
    events: CloudConnectorPeripheryEvent[];
    info: PeripheryInfo | null;
    withoutPresence: boolean | null;
    manualClose: boolean | null;
}

export interface OperatorZoneBarrierGatePeripheryDetails extends OperatorZoneBarrierGatePeriphery {
    presence: PresenceInfo | null;
    payments: PaymentsInfo | null;
}

interface PaymentsInfo {
    runningTransaction: PayStationTransactionInfo | null;
    lastCompletedTransactions: PayStationTransactionInfo[];
}

interface PayStationTransactionInfo {
    payStationPaymentId: string;
    quotedAt: string;
    identificationString: string;
    amount: number;
    state: PayStationPaymentState;
    paymentMethod: PayStationPaymentMethod | null;
    entryNameIfZoneHasMultiple: string | null;
}

interface PresenceInfo {
    presenceId: string;
    lprReadLicensePlate: string | null;
    identification: PresenceInfoIdentification | null;
    logInfo: PresenceLogInfo | null;
    runningEntryWithoutExit: RunningEntryWithoutExit | null;
    resolvedEntryWithoutExits: ResolvedEntryWithoutExit[];
    specialVehicle: SpecialVehicleType | null;
}

interface PresenceInfoIdentification {
    identificationMedia: IdentificationType;
    identificationString: string;
}

interface PresenceLogInfo {
    isSuccess: boolean;
    errorTranslation: Message | null;
}

interface RunningEntryWithoutExit {
    entryWithoutExitId: number;
    enteredAt: string;
    duration: string;
    paymentChannel: PaymentChannel;
    identificationMedia: IdentificationMedia;
    amountToBePaid: number;
    payStationPaymentsInfo: PayStationPaymentsInfo | null;
}

export interface PayStationPaymentsInfo {
    lastVisitedAt: string;
    amount: number;
    state: PayStationPaymentInfoState;
}

export enum PayStationPaymentInfoState {
    PAID = 'PAID',
    QUOTED = 'QUOTED',
}

interface ResolvedEntryWithoutExit {
    entryWithoutExitId: number;
    resolvedAt: string;
    resolvedReason: EntryResolvedReason;
    duration: string;
    identificationMedia: IdentificationMedia;
    paymentChannel: PaymentChannel;
}

export enum EntryResolvedReason {
    regular_exit = 'regular_exit',
    cleared_by_operator = 'cleared_by_operator',
    overwritten_by_entry = 'overwritten_by_entry',
    canceled_by_equipment = 'canceled_by_equipment',
    cleared_by_system = 'cleared_by_system',
    barrier_opened_by_operator = 'barrier_opened_by_operator',
}

type PeripheryInfo =
    | TicketRemainingInfo
    | PayStationDetailInfo
    | ExitHasEftTerminalInfo;

export enum PeripheryInfoType {
    TICKET_REMAINING = 'TICKET_REMAINING',
    PAY_STATION_INFO = 'PAY_STATION_INFO',
    HAS_EFT_TERMINAL = 'HAS_EFT_TERMINAL',
}

interface TicketRemainingInfo {
    peripheryInfoType: PeripheryInfoType.TICKET_REMAINING;
    ticketRemaining: number;
}

export interface PayStationDetailInfo {
    peripheryInfoType: PeripheryInfoType.PAY_STATION_INFO;
    hopperConfigType: HopperConfigType;
    hopperCoinCount: HopperCoinCount[];
    isCashless: boolean;
    cashboxWeightKg: number;
    cashboxTotalMoney: number;
    billCount: number;
    billCollectorTotalMoney: number;
}

interface ExitHasEftTerminalInfo {
    peripheryInfoType: PeripheryInfoType.HAS_EFT_TERMINAL;
}

export interface HopperCoinCount {
    coinType: CoinType;
    chfDenomination: number;
    numberOfCoins: number;
}

export enum CoinType {
    CHF_10 = 'CHF_10',
    CHF_20 = 'CHF_20',
    CHF_50 = 'CHF_50',
    CHF_100 = 'CHF_100',
    CHF_200 = 'CHF_200',
    CHF_500 = 'CHF_500',
}

function isCoinType(value?: string): value is CoinType {
    return Object.values(CoinType).includes(value as CoinType);
}

export function getCoinTypeByString(value?: string): CoinType | null {
    if (isCoinType(value)) {
        return value;
    }
    return null;
}

export function useZoneBarrierGatePeripheriesFetch(
    operatorId: number,
    refetchCounter: number,
) {
    return useServerFetch<
        OperatorPeripheriesList[],
        { operatorId: number; refetchCounter: number }
    >(
        ({ operatorId }) => ({
            url: `/ui-api/operator-account/${operatorId}/peripheries`,
        }),
        { operatorId: operatorId, refetchCounter: refetchCounter },
    );
}

function ZoneBarrierGatePeripheries() {
    const { currentLogin } = useOperatorContext();
    const [refetchCounter, setRefetchCounter] = useState(0);
    const [isVisible, setIsVisible] = useState(
        document.visibilityState === 'visible',
    );
    const [peripheriesListRequestState] = useZoneBarrierGatePeripheriesFetch(
        currentLogin.mandantId,
        refetchCounter,
    );

    // each poll loads the state of every periphery, so background tabs must not poll
    useDocumentVisibilityEffect(visibilityState => {
        const visible = visibilityState === 'visible';
        setIsVisible(visible);
        if (visible) {
            setRefetchCounter(prevCount => prevCount + 1);
        }
    });

    useEffect(() => {
        if (
            isVisible &&
            (peripheriesListRequestState.status === RequestStatus.SUCCESS ||
                peripheriesListRequestState.status === RequestStatus.ERROR)
        ) {
            const timeout = setTimeout(() => {
                setRefetchCounter(prevCount => prevCount + 1);
            }, 5000);
            return () => clearTimeout(timeout);
        }
        return undefined;
    }, [peripheriesListRequestState.status, refetchCounter, isVisible]);

    return (
        <OperatorAsyncLoadedSection
            requestState={peripheriesListRequestState}
            render={peripheriesList => (
                <Box sx={{ paddingBottom: '24px' }}>
                    {peripheriesList.map(parking => (
                        <ParkingSegment
                            parkingData={parking}
                            key={parking.zoneId}
                        />
                    ))}
                    <Outlet />
                </Box>
            )}
            spinnerFreeRefresh
        />
    );
}

export function PeripheriesOverviewDesktop() {
    const theme = useTheme();
    const { currentLogin, operatorExternalId } = useOperatorContext();
    const operatorId = currentLogin.mandantId;
    const { language } = useLanguage();
    return (
        <MainContent>
            <HeaderWithButtons
                title={
                    <Localized
                        de="Geräte"
                        fr="Appareils"
                        it="Apparecchi"
                        en="Devices"
                    />
                }
                icon={Dns}
                headerButtons={
                    <Stack
                        direction="row"
                        spacing={3}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <ButtonWithDropdown
                            label={
                                <Localized
                                    de="Exportieren"
                                    fr="Exporter"
                                    it="Esporta"
                                    en="Export"
                                />
                            }
                            itemVariant={'action'}
                            variant="outlined"
                            startIcon={<CloudDownload />}
                            dropdownItems={[
                                {
                                    key: 'unusual-exits',
                                    label: (
                                        <Localized
                                            de="Ungewöhnliche Bewegungen"
                                            fr="Mouvements atypiques"
                                            it="Movimenti anomali"
                                            en="Unusual movements"
                                        />
                                    ),
                                    disabled: false,
                                    onClickCallback: () => {
                                        openNewTab(
                                            urlWithOperatorExternalId(
                                                `/ui-api/operator-account/${operatorId}/unusual-exits/export/xlsx?language=${language}`,
                                                operatorExternalId,
                                            ),
                                        );
                                    },
                                    tooltip: (
                                        <Localized
                                            de="Es werden die Bewegungen (Ein- und Ausfahrten, Durchfahrten) protokolliert, bei denen die Barriere einmal geöffnet wurde und/oder die Gegensprechanlage betätigt wurde."
                                            fr="Les mouvements (entrées, sorties, transits) sont enregistrés lorsqu'il y a eu une ouverture individuelle de la barrière et/ou lorsque l'interphone a été actionné."
                                            it="Vengono protocollati i movimenti (entrate, uscite, transiti) dove c'é stata un'apertura singola della barriera e/o dove è stato premuto il citofono."
                                            en="Movements (entries, exits, transits) are logged where there has been a single opening of the barrier and/or where the intercom has been pressed."
                                        />
                                    ),
                                },
                                {
                                    key: 'disruption-statistics',
                                    label: (
                                        <Localized
                                            de="Störungsstatistik"
                                            fr="Statistiques des pannes"
                                            it="Statistica guasti"
                                            en="Disruption statistics"
                                        />
                                    ),
                                    disabled: false,
                                    onClickCallback: () => {
                                        openNewTab(
                                            urlWithOperatorExternalId(
                                                `/ui-api/operator-account/${operatorId}/disruption-statistics/export/xlsx?language=${language}`,
                                                operatorExternalId,
                                            ),
                                        );
                                    },
                                },
                            ]}
                        />
                        <Fullscreen
                            sx={{
                                fontSize: 32,
                                cursor: 'pointer',
                                color: theme.palette.blue.main,
                            }}
                            onClick={() =>
                                openNewTab(
                                    `/${operatorExternalId}/offstreet-devices/fullscreen`,
                                )
                            }
                        />
                    </Stack>
                }
            />
            <ZoneBarrierGatePeripheries />
        </MainContent>
    );
}

export function PeripheriesOverviewMobile() {
    return (
        <ParkingPortalLayoutWithHeader
            title={
                <Localized
                    de="Geräte"
                    fr="Appareils"
                    it="Apparecchi"
                    en="Devices"
                />
            }
            scrollable
            backTo={'..'}
        >
            <ZoneBarrierGatePeripheries />
        </ParkingPortalLayoutWithHeader>
    );
}

function ParkingSegment({
    parkingData,
}: {
    parkingData: OperatorPeripheriesList;
}) {
    return (
        <GroupSegment label={parkingData.name} direction={'column'}>
            <Stack
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    width: '100%',
                }}
                direction={{ sm: 'column', md: 'row' }}
                spacing={1}
            >
                <PeripheryColumn
                    configs={parkingData.peripheryConfigs.filter(
                        peripheryConfig =>
                            peripheryConfig.peripheryType ===
                            CloudConnectorPeripheryType.ENTRY,
                    )}
                />
                <PeripheryColumn
                    configs={parkingData.peripheryConfigs.filter(
                        peripheryConfig =>
                            peripheryConfig.peripheryType ===
                            CloudConnectorPeripheryType.PAY_STATION,
                    )}
                />
                <PeripheryColumn
                    configs={parkingData.peripheryConfigs.filter(
                        peripheryConfig =>
                            peripheryConfig.peripheryType ===
                            CloudConnectorPeripheryType.EXIT,
                    )}
                />
                <PeripheryColumn
                    configs={parkingData.peripheryConfigs.filter(
                        peripheryConfig =>
                            [
                                CloudConnectorPeripheryType.ACCESS,
                                CloudConnectorPeripheryType.TRANSIT_ENTRY,
                            ].includes(peripheryConfig.peripheryType),
                    )}
                />
            </Stack>
        </GroupSegment>
    );
}

function PeripheryColumn({
    configs,
}: {
    configs: OperatorZoneBarrierGatePeriphery[];
}) {
    if (configs.length === 0) return null;

    return (
        <Box
            sx={theme => ({
                display: 'flex',
                flexDirection: 'column',
                marginTop: '0px !important',
                marginLeft: '0px !important',
                width: '100%',
                paddingTop: '12px',
                '&:nth-child(1)': { paddingTop: 0 },
                [theme.breakpoints.up('md')]: {
                    width: '25%',
                    paddingTop: '0px',
                    paddingLeft: '12px',
                    '&:nth-child(1)': { paddingLeft: 0 },
                },
            })}
        >
            {configs.map(peripheryConfig => (
                <PeripheryDisplay
                    key={`${peripheryConfig.onPremiseId}-${peripheryConfig.peripheryType}`}
                    peripheryConfig={peripheryConfig}
                    playAlertSound={false}
                />
            ))}
        </Box>
    );
}

export function PeripheryDisplay({
    peripheryConfig,
    playAlertSound,
}: {
    peripheryConfig: OperatorZoneBarrierGatePeriphery;
    playAlertSound: boolean;
}) {
    const mainEvent = getMainEventfromEvents(peripheryConfig.events);
    const navigate = useNavigate();
    const detailPath = getPeripheryDetailPath(peripheryConfig);

    const [lastAlertedEventId, setLastAlertedEventId] = useState<number | null>(
        mainEvent?.category ===
            CloudConnectorPeripheryStateEventTypeCategory.ALARM
            ? mainEvent.id
            : null,
    );
    const [shouldBlink, setShouldBlink] = useState(false);

    const latestAlertedEventId =
        mainEvent != null &&
        mainEvent.category ===
            CloudConnectorPeripheryStateEventTypeCategory.ALARM &&
        mainEvent.id != lastAlertedEventId
            ? mainEvent.id
            : lastAlertedEventId;

    useEffect(() => {
        if (
            latestAlertedEventId != null &&
            lastAlertedEventId != latestAlertedEventId
        ) {
            const startTimeout = setTimeout(() => {
                setShouldBlink(true);
                setLastAlertedEventId(latestAlertedEventId);
                if (playAlertSound) {
                    const audio = new Audio('/alert.wav');
                    void audio.play();
                }
            }, 0);
            const stopTimeout = setTimeout(() => setShouldBlink(false), 10000);
            return () => {
                clearTimeout(startTimeout);
                clearTimeout(stopTimeout);
            };
        }
    }, [latestAlertedEventId, lastAlertedEventId, playAlertSound]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                minWidth: '288px',
                minHeight: '80px',
                borderRadius: '12px',
                marginTop: '12px',
                '&:nth-child(1)': { marginTop: 0 },
                cursor: detailPath !== null ? 'pointer' : 'default',
                overflow: 'hidden',
            }}
            onClick={
                detailPath !== null ? () => navigate(detailPath) : undefined
            }
        >
            <StatusIconContent
                status={getPeripheryStatefromMainCategory(
                    mainEvent?.category ?? null,
                )}
                shouldBlink={shouldBlink}
            />
            <Box
                sx={theme => ({
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    paddingLeft: '24px',
                    backgroundColor: theme.palette.common.white,
                    width: '100%',
                    flewGrow: 1,
                    '&:hover': {
                        backgroundColor: detailPath
                            ? theme.palette.grey[400]
                            : theme.palette.common.white,
                    },
                })}
            >
                <Box
                    sx={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: theme => theme.palette.primary.main,
                    }}
                >
                    {peripheryConfig.name}
                </Box>
                {mainEvent ? (
                    <Box
                        sx={{
                            fontSize: '16px',
                            fontWeight: 500,
                            color: theme => theme.palette.primary.main,
                        }}
                    >
                        <Localized
                            {...getCloudConnectorEventTypeTranslation(
                                mainEvent.eventType,
                            )}
                        />
                    </Box>
                ) : (
                    <PeripheryInfoContent info={peripheryConfig.info} />
                )}
            </Box>
        </Box>
    );
}

function PeripheryInfoContent({ info }: { info: PeripheryInfo | null }) {
    if (!info) {
        return null;
    }

    switch (info.peripheryInfoType) {
        case PeripheryInfoType.TICKET_REMAINING: {
            return (
                <InfoSection>
                    <ConfirmationNumberIcon width={17} height={14} />
                    <Localized
                        de={`${info.ticketRemaining} übrig`}
                        fr={`${info.ticketRemaining} restants`}
                        it={`${info.ticketRemaining} restanti`}
                        en={`${info.ticketRemaining} remaining`}
                    />
                </InfoSection>
            );
        }
        case PeripheryInfoType.PAY_STATION_INFO: {
            if (info.isCashless) {
                return (
                    <InfoSection>
                        <CreditCard width={17} height={14} />
                        <Localized
                            de="Cashless"
                            fr="Cashless"
                            it="Cashless"
                            en="Cashless"
                        />
                    </InfoSection>
                );
            } else {
                return (
                    <InfoSection>
                        <AccountBalanceWallet width={17} height={14} />
                        {`CHF ${(info.cashboxTotalMoney + info.billCollectorTotalMoney).toFixed(2)}`}
                    </InfoSection>
                );
            }
        }
        case PeripheryInfoType.HAS_EFT_TERMINAL: {
            return (
                <InfoSection>
                    <CreditCard width={17} height={14} />
                    <Localized
                        de="mit EFT-Terminal"
                        fr="avec terminal EFT"
                        it="con terminale EFT"
                        en="with EFT terminal"
                    />
                </InfoSection>
            );
        }
    }
}

function InfoSection({ children }: { children: ReactNode }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '16px',
                fontWeight: 500,
                color: theme => theme.palette.grey[600],
                gap: '3px',
            }}
        >
            {children}
        </Box>
    );
}

function getPeripheryDetailPath(
    peripheryConfig: OperatorZoneBarrierGatePeriphery,
): string | null {
    switch (peripheryConfig.peripheryType) {
        case CloudConnectorPeripheryType.ENTRY:
        case CloudConnectorPeripheryType.EXIT:
        case CloudConnectorPeripheryType.TRANSIT_ENTRY:
            return generatePath(
                'parking/:zoneId/periphery/:peripheryType/:onPremiseId/presence',
                {
                    zoneId: peripheryConfig.zoneId.toString(),
                    peripheryType: peripheryConfig.peripheryType,
                    onPremiseId: peripheryConfig.onPremiseId.toString(),
                },
            );
        case CloudConnectorPeripheryType.PAY_STATION:
        case CloudConnectorPeripheryType.ACCESS:
            return generatePath(
                'parking/:zoneId/periphery/:peripheryType/:onPremiseId/detail',
                {
                    zoneId: peripheryConfig.zoneId.toString(),
                    peripheryType: peripheryConfig.peripheryType,
                    onPremiseId: peripheryConfig.onPremiseId.toString(),
                },
            );
    }
}

export function getPeripheryStatusColor(
    status: PeripheryStatus,
    theme: Theme,
): string {
    switch (status) {
        case PeripheryStatus.OKAY:
            return theme.palette.success.main;
        case PeripheryStatus.ALERT:
            return theme.palette.warning.main;
        case PeripheryStatus.ERROR:
            return theme.palette.error.main;
    }
}

function StatusIconContent({
    status,
    shouldBlink,
}: {
    status: PeripheryStatus;
    shouldBlink: boolean;
}) {
    return (
        <Box
            sx={{
                display: 'flex',
                width: '80px',
                justifyContent: 'center',
                alignItems: 'center',
                flexShrink: 0,
                flexGrow: 1,
                backgroundColor: theme =>
                    getPeripheryStatusColor(status, theme),
                ...(shouldBlink && {
                    animation: 'blink 1s infinite',
                    '@keyframes blink': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                        '100%': { opacity: 1 },
                    },
                }),
            }}
        >
            <StatusIcon status={status} />
        </Box>
    );
}

function StatusIcon({ status }: { status: PeripheryStatus }) {
    switch (status) {
        case PeripheryStatus.OKAY:
            return (
                <CheckCircle
                    sx={{
                        fontSize: '55px',
                        color: theme => theme.palette.common.white,
                    }}
                />
            );
        case PeripheryStatus.ALERT:
            return (
                <ReportProblem
                    sx={{
                        fontSize: '55px',
                        color: theme => theme.palette.common.white,
                    }}
                />
            );
        case PeripheryStatus.ERROR:
            return (
                <Dangerous
                    sx={{
                        fontSize: '55px',
                        color: theme => theme.palette.common.white,
                    }}
                />
            );
    }
}
